import { Controller } from 'stimulus';

// Composer for formula-type sample_result_fields. Renders a contenteditable
// area with inline chips for dynamic references (fields and sample traits)
// and plain text for literal math. Serializes to the id-based token format
// consumed by FormulaTokens on the Rails side.
export default class extends Controller {
  static targets = ['composer', 'hiddenInput', 'picker', 'metadata', 'preview'];

  connect() {
    this.loadMetadata();
    this.deserialize(this.hiddenInputTarget.value || '');
    this.serialize();
    this.renderPreview();
  }

  loadMetadata() {
    const raw = this.metadataTarget.textContent || '{}';
    const parsed = JSON.parse(raw);
    this.namesByKey = {};
    this.unitsByKey = {};
    const groups = {
      field: parsed.fields || [],
      trait: parsed.traits || [],
      task_field: parsed.task_fields || [],
      task_parameter: parsed.task_parameters || [],
    };
    Object.entries(groups).forEach(([type, items]) => {
      items.forEach((item) => {
        this.namesByKey[`${type}:${item.id}`] = item.name;
        if (item.unit) this.unitsByKey[`${type}:${item.id}`] = item.unit;
      });
    });
  }

  deserialize(raw) {
    const composer = this.composerTarget;
    composer.innerHTML = '';
    if (!raw) return;

    const tokenRegex = /\{(field|trait|task_field|task_parameter):(\d+)\}/g;
    let lastIndex = 0;
    let match = tokenRegex.exec(raw);
    while (match !== null) {
      if (match.index > lastIndex) {
        composer.appendChild(document.createTextNode(raw.slice(lastIndex, match.index)));
      }
      composer.appendChild(this.buildChip(match[1], match[2]));
      lastIndex = tokenRegex.lastIndex;
      match = tokenRegex.exec(raw);
    }
    if (lastIndex < raw.length) {
      composer.appendChild(document.createTextNode(raw.slice(lastIndex)));
    }
  }

  paletteFor(type, missing) {
    if (missing) return 'bg-red-100 text-red-800 border-red-300';
    switch (type) {
      case 'field': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'trait': return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'task_field': return 'bg-green-100 text-green-800 border-green-300';
      case 'task_parameter': return 'bg-amber-100 text-amber-800 border-amber-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  }

  previewColorFor(type) {
    switch (type) {
      case 'field': return 'text-blue-700';
      case 'trait': return 'text-purple-700';
      case 'task_field': return 'text-green-700';
      case 'task_parameter': return 'text-amber-700';
      default: return 'text-gray-700';
    }
  }

  buildChip(type, id) {
    const key = `${type}:${id}`;
    const name = this.namesByKey[key];
    const missing = !name;

    const chip = document.createElement('span');
    chip.className = `formula-chip inline-block px-2 py-0.5 mx-0.5 rounded border text-xs font-medium align-middle ${this.paletteFor(type, missing)}`;
    chip.contentEditable = 'false';
    chip.dataset.token = `{${type}:${id}}`;
    chip.dataset.type = type;
    chip.dataset.id = id;
    chip.textContent = name || `${type}:${id}?`;
    return chip;
  }

  serialize() {
    let out = '';
    const walk = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        out += node.textContent;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.dataset && node.dataset.token) {
          out += node.dataset.token;
        } else if (node.tagName === 'BR') {
          out += ' ';
        } else {
          node.childNodes.forEach(walk);
        }
      }
    };
    this.composerTarget.childNodes.forEach(walk);
    this.hiddenInputTarget.value = out.trim();
  }

  onInput() {
    this.serialize();
    this.renderPreview();
  }

  onPickerChange(event) {
    const select = event.target;
    const value = select.value;
    if (!value) return;

    const [type, id] = value.split(':');
    this.insertChip(type, id);
    select.value = '';
  }

  insertChip(type, id) {
    const chip = this.buildChip(type, id);
    const trailing = document.createTextNode(' ');
    const composer = this.composerTarget;
    composer.focus();

    const selection = window.getSelection();
    if (
      selection.rangeCount > 0 &&
      composer.contains(selection.anchorNode)
    ) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      range.insertNode(trailing);
      range.insertNode(chip);
      range.setStartAfter(trailing);
      range.setEndAfter(trailing);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      composer.appendChild(chip);
      composer.appendChild(trailing);
    }

    this.serialize();
    this.renderPreview();
  }

  renderPreview() {
    if (!this.hasPreviewTarget) return;

    const raw = this.hiddenInputTarget.value || '';
    if (!raw.trim()) {
      this.previewTarget.innerHTML = '<span class="text-gray-400 italic">—</span>';
      return;
    }

    try {
      const tokens = this.tokenize(raw);
      const ast = this.parseExpression(tokens);
      if (tokens.pos < tokens.length) throw new Error('trailing input');
      const mathHtml = this.renderNode(ast);
      const derivedUnit = this.deriveUnit(ast);
      const unitHtml = derivedUnit
        ? `<div class="mt-1 text-xs text-gray-500">Derived unit: <span class="font-medium text-gray-700">${this.escapeHtml(derivedUnit)}</span></div>`
        : '';
      this.previewTarget.innerHTML = mathHtml + unitHtml;
    } catch (err) {
      this.previewTarget.innerHTML = `<span class="text-red-500 italic">Can't preview: ${this.escapeHtml(err.message)}</span>`;
    }
  }

  tokenize(input) {
    const tokens = [];
    let i = 0;
    while (i < input.length) {
      const ch = input[i];
      if (/\s/.test(ch)) { i += 1; continue; }

      if (ch === '{') {
        const end = input.indexOf('}', i);
        if (end === -1) throw new Error('unclosed reference');
        const body = input.slice(i + 1, end);
        const m = body.match(/^(field|trait|task_field|task_parameter):(\d+)$/);
        if (!m) throw new Error(`bad reference: ${body}`);
        tokens.push({ kind: 'ref', type: m[1], id: parseInt(m[2], 10) });
        i = end + 1;
        continue;
      }

      if (/[0-9.]/.test(ch)) {
        let j = i;
        while (j < input.length && /[0-9.]/.test(input[j])) j += 1;
        tokens.push({ kind: 'num', value: input.slice(i, j) });
        i = j;
        continue;
      }

      if ('+-*/^()'.includes(ch)) {
        tokens.push({ kind: 'op', value: ch });
        i += 1;
        continue;
      }

      throw new Error(`unexpected character: ${ch}`);
    }
    tokens.pos = 0;
    return tokens;
  }

  peek(tokens) { return tokens[tokens.pos]; }
  consume(tokens) { return tokens[tokens.pos++]; }

  parseExpression(tokens) {
    let left = this.parseTerm(tokens);
    while (true) {
      const t = this.peek(tokens);
      if (!t || t.kind !== 'op' || (t.value !== '+' && t.value !== '-')) break;
      this.consume(tokens);
      const right = this.parseTerm(tokens);
      left = { kind: 'binop', op: t.value, left, right };
    }
    return left;
  }

  parseTerm(tokens) {
    let left = this.parseFactor(tokens);
    while (true) {
      const t = this.peek(tokens);
      if (!t || t.kind !== 'op' || (t.value !== '*' && t.value !== '/')) break;
      this.consume(tokens);
      const right = this.parseFactor(tokens);
      left = { kind: 'binop', op: t.value, left, right };
    }
    return left;
  }

  parseFactor(tokens) {
    const base = this.parseBase(tokens);
    const t = this.peek(tokens);
    if (t && t.kind === 'op' && t.value === '^') {
      this.consume(tokens);
      const exponent = this.parseFactor(tokens);
      return { kind: 'binop', op: '^', left: base, right: exponent };
    }
    return base;
  }

  parseBase(tokens) {
    const t = this.peek(tokens);
    if (!t) throw new Error('unexpected end');

    if (t.kind === 'op' && t.value === '-') {
      this.consume(tokens);
      return { kind: 'unary', op: '-', operand: this.parseBase(tokens) };
    }

    if (t.kind === 'op' && t.value === '(') {
      this.consume(tokens);
      const inner = this.parseExpression(tokens);
      const close = this.consume(tokens);
      if (!close || close.value !== ')') throw new Error('missing )');
      return { kind: 'group', inner };
    }

    if (t.kind === 'num') {
      this.consume(tokens);
      return { kind: 'num', value: t.value };
    }

    if (t.kind === 'ref') {
      this.consume(tokens);
      return { kind: 'ref', type: t.type, id: t.id };
    }

    throw new Error(`unexpected token: ${t.value || t.kind}`);
  }

  renderNode(node) {
    switch (node.kind) {
      case 'num':
        return this.escapeHtml(node.value);
      case 'ref': {
        const key = `${node.type}:${node.id}`;
        const name = this.namesByKey[key];
        if (!name) return `<span class="italic text-red-600">${this.escapeHtml(key)}?</span>`;
        return `<span class="italic font-medium ${this.previewColorFor(node.type)}">${this.escapeHtml(name)}</span>`;
      }
      case 'group':
        return `<span>(${this.renderNode(node.inner)})</span>`;
      case 'unary':
        return `<span>−${this.renderNode(node.operand)}</span>`;
      case 'binop':
        return this.renderBinop(node);
      default:
        return '';
    }
  }

  renderBinop(node) {
    const L = this.renderNode(node.left);
    const R = this.renderNode(node.right);
    switch (node.op) {
      case '+':
        return `<span>${L} + ${R}</span>`;
      case '-':
        return `<span>${L} − ${R}</span>`;
      case '*':
        return `<span>${L} · ${R}</span>`;
      case '^':
        return `<span>${L}<sup style="font-size:0.75em;">${R}</sup></span>`;
      case '/':
        return (
          '<span style="display:inline-flex; flex-direction:column; vertical-align:middle; text-align:center; margin:0 0.25em;">' +
            `<span style="border-bottom:1px solid currentColor; padding:0 0.3em;">${L}</span>` +
            `<span style="padding:0 0.3em;">${R}</span>` +
          '</span>'
        );
      default:
        return `<span>${L} ${node.op} ${R}</span>`;
    }
  }

  deriveUnit(node) {
    switch (node.kind) {
      case 'num':
        return null; // dimensionless
      case 'ref': {
        const key = `${node.type}:${node.id}`;
        return this.unitsByKey[key] || null;
      }
      case 'group':
        return this.deriveUnit(node.inner);
      case 'unary':
        return this.deriveUnit(node.operand);
      case 'binop': {
        const lu = this.deriveUnit(node.left);
        const ru = this.deriveUnit(node.right);
        switch (node.op) {
          case '+':
          case '-':
            // Same dimension — return whichever is defined
            return lu || ru;
          case '*':
            if (!lu) return ru;
            if (!ru) return lu;
            return `${lu}\u00b7${ru}`;
          case '/':
            if (!lu && !ru) return null;
            if (!ru) return lu;
            if (!lu) return `1/${ru}`;
            return `${lu}/${ru}`;
          case '^': {
            if (!lu) return null;
            const exp = this.exponentText(node.right);
            return exp ? `${lu}^${exp}` : lu;
          }
          default:
            return lu || ru;
        }
      }
      default:
        return null;
    }
  }

  exponentText(node) {
    if (node.kind === 'num') return node.value;
    if (node.kind === 'unary' && node.operand.kind === 'num') return `-${node.operand.value}`;
    return null;
  }

  escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
